import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  Modal,
  Box,
  Typography,
  CircularProgress,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import TextInput from 'src/components/UI/input/TextInput';
import RadioInput from 'src/components/UI/input/RadioInput';
import { t } from 'i18next';
import ClinicalNoteDeleteModal from '../diagnoses-menu/ClinicalNoteDeleteModal';
import { calculateAge } from 'src/util/util';
import { CLINICAL_RECORD_DIALOG_WIDTH } from 'src/components/UI/dialog/CustomDialog';

const PhenotypeModal = ({
  open,
  onClose,
  phenotype,
  onSave,
  user,
  onDeletePhenotype,
  isDisabled,
  patientDob,
  staffMemberNames = [],
  onShowHistory
}) => {
  const [formValues, setFormValues] = useState({
    observed: true,
    clinicallyConfirmed: false,
    patientReported: false,
    isPrivate: false,
    displayOnPedigree: true,
    dateReported: '',
    recordedBy: '',
    recordedAge: '',
    comment: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  const computeAge = useCallback(
    (dateReported) => {
      if (!patientDob || !dateReported) return '';
      const parsedDate = new Date(dateReported);
      if (Number.isNaN(parsedDate.getTime())) return '';
      return calculateAge(patientDob, parsedDate);
    },
    [patientDob]
  );

  useEffect(() => {
    if (phenotype) {
      setFormValues({
        observed: phenotype.observed ?? true,
        clinicallyConfirmed: phenotype.clinicallyConfirmed || false,
        patientReported: phenotype.patientReported || false,
        isPrivate: phenotype.isPrivate || false,
        displayOnPedigree: phenotype.displayOnPedigree ?? true,
        dateReported: phenotype.dateRecorded || '',
        recordedBy:
          phenotype.recordedBy ||
          `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        // Always seed from the saved age. The sync effect below recomputes
        // when the user changes the date, but the user's saved age is never
        // overwritten just because no date was recorded.
        recordedAge: phenotype.recordedAge ?? '',
        comment: phenotype.comment || ''
      });
    }
  }, [phenotype, user]);

  useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAge(formValues.dateReported);
    // Only apply a date-derived age — never erase a saved age because the
    // date field is empty. The inline-capture path stores age without a
    // date, and we must preserve it on re-hydration.
    if (nextAge === '') return;
    setFormValues((prev) => {
      if (String(nextAge) === String(prev.recordedAge ?? '')) return prev;
      return { ...prev, recordedAge: nextAge };
    });
  }, [patientDob, computeAge, formValues.dateReported]);

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    if (!name) return;
    if (name === 'dateReported' && patientDob) {
      const age = computeAge(value);
      setFormValues((prev) => ({
        ...prev,
        dateReported: value,
        recordedAge: age !== '' ? age : prev.recordedAge
      }));
      return;
    }
    if (name === 'observed') {
      // MUI RadioGroup delivers value as a string; coerce back to boolean so
      // the strict-equality check in the tile renderer matches.
      const observed = value === true || value === 'true';
      setFormValues((prev) => ({
        ...prev,
        observed,
        displayOnPedigree: observed
      }));
      return;
    }
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    onSave({
      ...phenotype,
      observed: formValues.observed,
      clinicallyConfirmed: formValues.clinicallyConfirmed,
      patientReported: formValues.patientReported,
      isPrivate: formValues.isPrivate,
      displayOnPedigree: formValues.displayOnPedigree,
      dateRecorded: formValues.dateReported,
      recordedBy: formValues.recordedBy,
      recordedAge:
        formValues.recordedAge === ''
          ? undefined
          : Number(formValues.recordedAge),
      comment: formValues.comment?.trim() || undefined
    });
  };

  const handleOpenModal = () => {
    setOpenDeleteModal(true);
  };

  const handleCloseModal = () => {
    setOpenDeleteModal(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await onDeletePhenotype(phenotype.id);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setDeleteLoading(false);
      handleCloseModal();
    }
  };

  return (
    <>
      <ClinicalNoteDeleteModal
        loading={deleteLoading}
        open={openDeleteModal}
        handleCloseModal={handleCloseModal}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this Phenotype ?"
        content="Warning: This action will permanently delete this Phenotype. Once deleted, this
                  information will be permanently removed and cannot be restored."
      />
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            // Match the shared clinical-record / disease modal width so the
            // phenotype editor reads as part of the same family of dialogs.
            width: CLINICAL_RECORD_DIALOG_WIDTH,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 1
          }}
        >
          {/* 顶栏：使用系统主题的 secondary.main 来跟随主题色 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', textAlign: 'left', color: '#fff' }}
            >
              {t('Edit Phenotype')}
            </Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* 实际内容区：空出一点上边距，与顶栏分隔 */}
          <Box sx={{ p: 2, pt: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Typography variant="body1">
                  <strong>{t('Name')}:</strong> {phenotype?.name}
                </Typography>
                {phenotype?.id && (
                  <Chip
                    label={phenotype.id}
                    clickable
                    onClick={() =>
                      window.open(
                        `https://hpo.jax.org/browse/term/${encodeURIComponent(
                          phenotype.id
                        )}`,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 400,
                      border: 'none',
                      color: '#1a7a8a',
                      backgroundColor: 'rgba(43, 169, 191, 0.15)',
                      borderRadius: '10px',
                      height: 'auto',
                      py: 0.5
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Date Observed, Recorded By, and Age When Observed */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <TextInput
                  type="date"
                  label={t('Date Observed')}
                  name="dateReported"
                  value={formValues.dateReported}
                  // DateInput delivers the Date value directly, not a DOM
                  // event — wrap it so handleChange's name-based routing works.
                  onChange={(date) =>
                    handleChange({
                      target: { name: 'dateReported', value: date }
                    })
                  }
                  disableFuture
                />
              </Grid>
              <Grid size={6}>
                <TextInput
                  label={t('Recorded By')}
                  name="recordedBy"
                  type="autocomplete"
                  value={formValues.recordedBy}
                  options={staffMemberNames}
                  onChange={(newValue) => {
                    const label = newValue ? newValue.label || newValue : '';
                    handleChange({
                      target: { name: 'recordedBy', value: label }
                    });
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mb: 2 }}>
              <TextInput
                label={t('Age When Observed')}
                name="recordedAge"
                type="number"
                value={formValues.recordedAge ?? ''}
                // Disabled (muted) only when an age is actually being
                // auto-calculated, i.e. DoB AND date reported both set.
                // Editable when either is missing so a known age can be
                // recorded directly.
                disabled={!!patientDob && !!formValues.dateReported}
                onChange={
                  patientDob && formValues.dateReported
                    ? undefined
                    : (e) => {
                        const val = e.target.value;
                        const parsed = val === '' ? '' : Number(val);
                        if (val === '' || !Number.isNaN(parsed)) {
                          setFormValues((prev) => ({
                            ...prev,
                            recordedAge: val === '' ? '' : parsed
                          }));
                        }
                      }
                }
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <RadioInput
                label={t('Phenotype Status')}
                name="observed"
                onChange={handleChange}
                options={[
                  { label: t('Present'), value: true },
                  { label: t('Explicitly Absent'), value: false }
                ]}
                value={formValues.observed}
                helperText={t(
                  'Phenotypes default to Present. Set to Explicitly Absent for findings that have been assessed and ruled out.'
                )}
                row
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <RadioInput
                  label={t('Clinically Confirmed')}
                  name="clinicallyConfirmed"
                  onChange={handleChange}
                  options={yesNoOptions}
                  value={formValues.clinicallyConfirmed}
                  row
                />
              </Grid>
              <Grid size={6}>
                <RadioInput
                  label={t('Patient Reported')}
                  name="patientReported"
                  onChange={handleChange}
                  options={yesNoOptions}
                  value={formValues.patientReported}
                  row
                />
              </Grid>
              <Grid size={6}>
                <RadioInput
                  label={t('Private')}
                  name="isPrivate"
                  onChange={handleChange}
                  options={yesNoOptions}
                  value={formValues.isPrivate}
                  row
                />
              </Grid>
              <Grid size={6}>
                <RadioInput
                  label={t('Display on Pedigree')}
                  name="displayOnPedigree"
                  onChange={handleChange}
                  options={yesNoOptions}
                  value={formValues.displayOnPedigree}
                  row
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <TextInput
                type="textarea"
                label={t('Comments')}
                name="comment"
                value={formValues.comment || ''}
                onChange={handleChange}
                rows={3}
              />
            </Box>

            {/* 底部按钮 */}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleOpenModal}
                disabled={isDisabled || deleteLoading}
                endIcon={
                  deleteLoading ? <CircularProgress size={15} /> : undefined
                }
              >
                {t('Delete')}
              </Button>
              <Button variant="outlined" colour="secondary" onClick={onClose}>
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                colour="secondary"
                onClick={handleSave}
                disabled={isDisabled}
              >
                {t('Save')}
              </Button>
            </Box>
            {onShowHistory && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <IconButton
                  size="small"
                  title={t('Record history')}
                  onClick={onShowHistory}
                >
                  <HistoryIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PhenotypeModal;
